import { Button } from "@/components/core/buttons/button";
import Tooltip from "@/components/core/tooltip";
import { IconFileDownload, IconRotate, IconX, IconZoomIn, IconZoomOut } from "@tabler/icons-react";
import { useState } from "react";

interface SimpleImageViewerProps {
	imageUrl: string;
	fileName: string;
	onDownload?: () => void;
}

export function SimpleImageViewer({ imageUrl, fileName, onDownload }: SimpleImageViewerProps) {
	const [zoom, setZoom] = useState(1);
	const [rotation, setRotation] = useState(0);
	const [fullscreen, setFullscreen] = useState(false);

	const handleZoomIn = () => {
		setZoom((prev) => Math.min(prev + 0.2, 3));
	};

	const handleZoomOut = () => {
		setZoom((prev) => Math.max(prev - 0.2, 0.2));
	};

	const handleRotate = () => {
		setRotation((prev) => (prev + 90) % 360);
	};

	const handleDownload = () => {
		if (onDownload) {
			onDownload();
		}
	};

	return (
		<div className="flex flex-col w-full h-full bg-background">
			{/* Header */}
			<div className="flex items-center justify-between p-4 border-b border-slate-200">
				<span className="text-base font-bold text-foreground-dark">{fileName}</span>
				<div className="flex items-center gap-2">
					<Tooltip content="Zoom out" side="bottom">
						<Button onClick={handleZoomOut} type="button" size="icon" variant="ghost" disabled={zoom <= 0.2}>
							<IconZoomOut size={18} stroke={1.5} />
						</Button>
					</Tooltip>
					<span className="text-sm font-medium min-w-[60px] text-center">{Math.round(zoom * 100)}%</span>
					<Tooltip content="Zoom in" side="bottom">
						<Button onClick={handleZoomIn} type="button" size="icon" variant="ghost" disabled={zoom >= 3}>
							<IconZoomIn size={18} stroke={1.5} />
						</Button>
					</Tooltip>
					<Tooltip content="Rotate" side="bottom">
						<Button onClick={handleRotate} type="button" size="icon" variant="ghost">
							<IconRotate size={18} stroke={1.5} />
						</Button>
					</Tooltip>
					{onDownload && (
						<Tooltip content="Download" side="bottom">
							<Button onClick={handleDownload} type="button" size="icon" variant="ghost">
								<IconFileDownload size={18} stroke={1.5} />
							</Button>
						</Tooltip>
					)}
					<Tooltip content="Full screen" side="bottom">
						<Button onClick={() => setFullscreen(true)} type="button" size="icon" variant="ghost">
							<svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} role="img" aria-label="Fullscreen">
								<path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
							</svg>
						</Button>
					</Tooltip>
				</div>
			</div>

			{/* Image Container */}
			<div className="flex-1 flex items-center justify-center p-4 bg-gray-50 overflow-auto">
				<div
					className="flex items-center justify-center"
					style={{
						transform: `scale(${zoom}) rotate(${rotation}deg)`,
						transition: "transform 0.2s ease-in-out",
					}}
				>
					<img
						src={imageUrl}
						alt={fileName}
						className="max-w-none max-h-none object-contain"
						style={{
							maxWidth: fullscreen ? "none" : "100%",
							maxHeight: fullscreen ? "none" : "100%",
						}}
						onLoad={() => {
							// Reset zoom if image is very small
							const img = new Image();
							img.onload = () => {
								if (img.width < 300 && img.height < 300) {
									setZoom(1.5);
								}
							};
							img.src = imageUrl;
						}}
					/>
				</div>
			</div>

			{/* Fullscreen Modal */}
			{fullscreen && (
				<div className="fixed inset-0 z-[999] bg-black flex items-center justify-center">
					<button type="button" className="absolute top-4 right-4 text-white z-10" onClick={() => setFullscreen(false)}>
						<IconX size={32} stroke={1.5} />
					</button>
					<div
						className="flex items-center justify-center w-full h-full p-8"
						style={{
							transform: `scale(${zoom}) rotate(${rotation}deg)`,
							transition: "transform 0.2s ease-in-out",
						}}
					>
						<img src={imageUrl} alt={fileName} className="max-w-full max-h-full object-contain" />
					</div>
				</div>
			)}
		</div>
	);
}
